import LinearScaleIcon from '@mui/icons-material/LinearScale'
import SaveIcon from '@mui/icons-material/Save'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useEffect, useState } from 'react'
import {
  Button,
  Create,
  FormTab,
  ListButton,
  TabbedForm,
  TopToolbar,
  useNotify,
  useRedirect,
  useSidebarState,
} from 'react-admin'
import { Link, useLocation } from 'react-router-dom'
import { GuidedBoatForm, ToggleNotesButton, UpsertBoat, UpsertBoatAttributes } from '~/components'
import { GUIDED_FORM_ID, guidedFormRoot, isGuidedPath } from '~/helpers'

const RemoteSaveButton = ({ saveProps }) => {
  const { invalid, pristine, saving } = saveProps
  return (
    <Button
      type='submit'
      form={GUIDED_FORM_ID}
      variant='text'
      size='small'
      disabled={invalid || pristine || saving}
      label='ra.action.save'
    >
      <SaveIcon />
    </Button>
  )
}

const BoatCreateActions = ({
  data,
  guided,
  saveProps,
  showToggleNotes,
  showNotes,
  setShowNotes,
}) => (
  <TopToolbar>
    {saveProps && <RemoteSaveButton saveProps={saveProps} />}
    {showToggleNotes && <ToggleNotesButton showNotes={showNotes} setShowNotes={setShowNotes} />}
    {!guided && (
      <Button component={Link} to='/boats/create/guided' label='resources.boats.guided_input'>
        <LinearScaleIcon />
      </Button>
    )}
    <ListButton label='ra.action.back' />
  </TopToolbar>
)

const BoatCreate = (props) => {
  const { pathname } = useLocation()
  const guided = isGuidedPath(pathname)
  const firstTabMatch = pathname.endsWith('/1')
  const [open, setSidebarOpen] = useSidebarState()
  const openSidebar = () => setSidebarOpen(true)
  const closeSidebar = () => setSidebarOpen(false)
  const [saveProps, setSaveProps] = useState(null)
  const [showNotes, setShowNotes] = useState(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (guided && !open) openSidebar()
  }, [guided, open, openSidebar])

  const notify = useNotify()
  const redirect = useRedirect()

  // react-admin 5: onSuccess receives the record, and goes in mutationOptions.
  // (The paths were built from basePath, gone since react-admin 4, and led to
  // /undefined/….)
  const onSuccessGuided = (data) => {
    notify('ra.notification.created', { messageArgs: { smart_count: 1 } })
    redirect(`/boats/${data.id}/guided`)
  }

  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down('xs'))

  return guided ? (
    <Create
      title='resources.boats.create'
      defaultValue={{ created_at: Date.now() }}
      actions={<BoatCreateActions guided saveProps={saveProps} />}
      {...props}
      style={isXSmall ? {} : { marginLeft: open ? '244px' : '60px' }}
      mutationOptions={{ onSuccess: onSuccessGuided }}
      key='guided_create' // ensures remounting when switching to guided input
    >
      <GuidedBoatForm
        formRootPathname={guidedFormRoot(pathname)}
        warnWhenUnsavedChanges
        saveProps={saveProps}
        setSaveProps={setSaveProps}
      />
    </Create>
  ) : (
    <Create
      title='resources.boats.create'
      defaultValue={{ created_at: Date.now() }}
      actions={
        <BoatCreateActions
          showToggleNotes={!!firstTabMatch}
          showNotes={showNotes}
          setShowNotes={setShowNotes}
        />
      }
      {...props}
      redirect='show'
    >
      <TabbedForm warnWhenUnsavedChanges>
        <FormTab label='resources.boats.base'>
          <UpsertBoat {...props} />
        </FormTab>
        <FormTab label='resources.boats.attributes'>
          <UpsertBoatAttributes {...props} showNotes={showNotes} />
        </FormTab>
      </TabbedForm>
    </Create>
  )
}

export default BoatCreate
